import { collection, addDoc, query, where, getDocs, doc, serverTimestamp, Timestamp, getDoc, updateDoc, deleteDoc, writeBatch, orderBy, startAfter, limit, onSnapshot, setDoc, collectionGroup } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';

export interface Column {
  id: string;
  header: string;
  width?: number;
}

export interface Proof {
  url: string;
  description: string;
}

export enum CellState {
  EMPTY = 'empty',
  LOADING = 'loading',
  READY = 'ready',
  ERROR = 'error',
  EDITED = 'edited',
  HAS_VALUE = 'has_value',
  NOT_FOUND = 'not_found',
  DELETED = 'deleted'
}

export interface CellProof {
  url: string;              // Source URL
  timestamp: string;       // When this proof was found/verified
}

export interface CellError {
  message: string;         // Error message
  code: string;           // Error code
  timestamp: string;      // When this error occurred
}

export interface Cell {
  id: string;
  tableId: string;
  rowIndex: number;
  columnIndex: number;
  value: string;
  /** Optional free-text instructions for header cells (row 0 or column 0). */
  instructions?: string;
  detailedValue?: string;
  proof?: string | CellProof;
  state: CellState;
  confidence?: number;
  error?: CellError;
  updatedAt: string;
  updatedBy?: string;
}

export interface Row {
  id: string;
  cells: { [columnIndex: number]: Cell };
  createdAt: string;
  updatedAt: string;
}

/** A permission entry stored in the separate tablePermissions collection */
export interface TablePermission {
  id?: string;
  tableId: string;
  email: string; // Email address (required)
  role: 'viewer' | 'editor' | 'owner';
  grantedBy: string; // userId of who granted this permission
  grantedAt: string; // ISO timestamp
}

export interface Table {
  id: string;
  name: string;
  description?: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  rows?: number;
  columns?: number;
  /** Public / link / domain level access configuration */
  access?: TableAccessConfig;
  /** Loaded permissions for this table (not stored in table doc) */
  permissions?: TablePermission[];
  /** Automatic update schedule configuration */
  schedule?: {
    frequency: number; // 0 = disabled, 1 = daily, 7 = weekly, 30 = monthly
    lastUpdate: string; // ISO timestamp
  };
}

/** Additional access configuration for the table. */
export interface TableAccessConfig {
  /** When true the table can be discovered and viewed by anyone */
  public?: boolean;
  /** When true anyone with the secret linkId can access (at least view) */
  anyoneWithLink?: boolean;
  /** Random identifier that forms part of the shareable link */
  linkId?: string;
  /** Allow anyone whose email ends with this domain to access */
  domain?: string;
  /** Role granted to domain-level users (viewer | editor) */
  domainRole?: 'viewer' | 'editor';
  /** Role granted to the anonymous link (viewer | editor) */
  linkRole?: 'viewer' | 'editor';
}

/** Utilities for new roles subcollection **/

const roleDocRef = (tableId: string, email: string) => doc(db, `tables/${tableId}/roles/${email}`);

const getTableRoles = async (tableId: string): Promise<TablePermission[]> => {
  try {
    const rolesRef = collection(db, `tables/${tableId}/roles`);
    const snapshot = await getDocs(rolesRef);
    return snapshot.docs.map(d => ({ id: d.id, ...(d.data() as TablePermission) }));
  } catch (err) {
    console.error('Error getting table roles:', err);
    return [];
  }
};

export const getUserTablePermission = async (email: string, tableId: string): Promise<TablePermission | null> => {
  try {
    const docRef = roleDocRef(tableId, email);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...(snap.data() as TablePermission) };
  } catch (err) {
    console.error('Error getting user table role:', err);
    return null;
  }
};

const setUserTablePermission = async (tableId: string, email: string, role: 'viewer' | 'editor' | 'owner', grantedBy: string): Promise<void> => {
  try {
    const docRef = roleDocRef(tableId, email);
    await setDoc(docRef, { email, role, tableId, grantedBy, grantedAt: new Date().toISOString() });
  } catch (err) {
    console.error('Error setting user table role:', err);
    throw err;
  }
};

const removeUserTablePermission = async (tableId: string, email: string): Promise<void> => {
  try {
    await deleteDoc(roleDocRef(tableId, email));
  } catch (err) {
    console.error('Error removing user table role:', err);
    throw err;
  }
};

export const getUserPermissionTables = async (email: string): Promise<string[]> => {
  try {
    const rolesGroup = collectionGroup(db, 'roles');
    const qSnap = await getDocs(query(rolesGroup, where('email', '==', email)));
    return qSnap.docs.map(d => (d.ref.parent.parent?.id) as string).filter(Boolean);
  } catch (err) {
    console.error('Error getting user role tables:', err);
    return [];
  }
};

/**
 * Check if user has owner access (creator or explicitly granted owner role)
 */
const hasOwnerAccess = async (userId: string | null, table: any, userEmail?: string | null): Promise<boolean> => {
  if (!table || !userId) return false;
  
  // Original creator is always an owner
  if (table.userId === userId) return true;

  // Check for explicit owner permissions
  if (table.id) {
    // Use provided email or fallback to auth.currentUser?.email
    const emailToCheck = userEmail !== undefined ? userEmail : (auth.currentUser?.email || null);

    if (emailToCheck) {
      const userPermission = await getUserTablePermission(emailToCheck, table.id);
      if (userPermission && userPermission.role === 'owner') {
        return true;
      }
    }
  }

  return false;
};

/**
 * Check if user has read access (includes permissions check)
 */
const hasReadAccess = async (userId: string | null, table: any, userEmail?: string | null): Promise<boolean> => {
  if (!table) return false;
  
  // Owner has full access
  if (userId && table.userId === userId) return true;

  // Check explicit permissions (only for authenticated users)
  if (userId && table.id) {
    const emailToCheck = userEmail !== undefined ? userEmail : (auth.currentUser?.email || null);

    if (emailToCheck) {
      // For non-owner users we only fetch their own permission document
      const userPermission = await getUserTablePermission(emailToCheck, table.id);
      if (userPermission) {
        console.log(`[Permission Check] User permission (${userPermission.role}) grants read access`);
        return true;
      }
    } else {
      console.warn(`[Permission Check] No email available for user ${userId}`);
    }
  }

  // Public access (allows unauthenticated users to read)
  if (table.access?.public) return true;

  // Link-level access (allows unauthenticated users with link to read)
  if (table.access?.anyoneWithLink) return true;

  return false;
};

/**
 * Check if user has write access (includes permissions check)
 * General access (public/link) is always read-only, only explicit permissions grant write access
 */
const hasWriteAccess = async (userId: string | null, table: any, userEmail?: string | null): Promise<boolean> => {
  if (!table) return false;
  // Owner can always edit
  if (userId && table.userId === userId) return true;

  // Only authenticated users can have write access
  if (!userId) return false;

  // Check explicit permissions for editor or owner role
  if (table.id) {
    // Use provided email or fallback to auth.currentUser?.email
    const emailToCheck = userEmail !== undefined ? userEmail : (auth.currentUser?.email || null);

    if (emailToCheck) {
      const userPermission = await getUserTablePermission(emailToCheck, table.id);
      if (userPermission && (userPermission.role === 'editor' || userPermission.role === 'owner')) {
        return true;
      }
    }
  }

  // General access (public/link) is always read-only regardless of linkRole
  // This ensures that people with no account or no specific edit permission can never edit
  return false;
};

interface FirestoreCell extends Omit<Cell, 'updatedAt' | 'id'> {
  updatedAt: Timestamp;
}

interface FirestoreTable extends Omit<Table, 'createdAt' | 'updatedAt' | 'id' | 'permissions'> {
  createdAt: Timestamp;
  updatedAt: Timestamp;
  access?: TableAccessConfig;
  schedule?: {
    frequency: number;
    lastUpdate: string;
  };
}

/**
 * Get the current user's permission level for a table
 * Returns 'owner', 'editor', 'viewer', or null if no access
 */
const getUserPermissionLevel = async (userId: string | null, table: any, userEmail?: string | null): Promise<'owner' | 'editor' | 'viewer' | null> => {
  if (!table) return null;
  
  // Original creator is always an owner
  if (userId && table.userId === userId) return 'owner';

  // Check explicit permissions for authenticated users
  if (userId && table.id) {
    // Use provided email or fallback to auth.currentUser?.email
    const emailToCheck = userEmail !== undefined ? userEmail : (auth.currentUser?.email || null);

    if (emailToCheck) {
      const userPermission = await getUserTablePermission(emailToCheck, table.id);
      if (userPermission) return userPermission.role;
    }
  }

  // Check general access (public/link) - always viewer level
  if (table.access?.public || table.access?.anyoneWithLink) {
    return 'viewer';
  }

  return null;
};

export const tablesService = {
  async deleteTable(userId: string, tableId: string): Promise<void> {
    try {
      // First verify table ownership
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        throw new Error('Table not found');
      }
      
      if (tableDoc.data()?.userId !== userId) {
        throw new Error('Unauthorized');
      }

      // Get all cells for this table
      const cellsRef = collection(db, 'cells');
      const q = query(cellsRef, where('tableId', '==', tableId));
      const cellsSnapshot = await getDocs(q);
      
      // Delete cells in batches of 500 (Firestore batch limit)
      const batchSize = 500;
      const batches = Math.ceil(cellsSnapshot.size / batchSize);
      
      for (let i = 0; i < batches; i++) {
        const batch = writeBatch(db);
        const batchDocs = cellsSnapshot.docs.slice(i * batchSize, (i + 1) * batchSize);
        
        batchDocs.forEach(doc => {
          batch.delete(doc.ref);
        });
        
        await batch.commit();
      }

      // Finally delete the table document
      await deleteDoc(tableRef);
    } catch (error) {
      console.error('Error deleting table:', error);
      throw error;
    }
  },

  async deleteCell(userId: string, tableId: string, cellId: string): Promise<void> {
    try {
      // First verify table ownership
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      const userEmail = auth.currentUser?.email || null;
      if (!tableDoc.exists() || !await hasWriteAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Table not found or unauthorized');
      }

      // Delete the cell
      const cellRef = doc(db, 'cells', cellId);
      await deleteDoc(cellRef);
    } catch (error) {
      console.error('Error deleting cell:', error);
      throw error;
    }
  },

  async getTable(userId: string, tableId: string): Promise<Table | null> {
    try {
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        return null;
      }

      const data = tableDoc.data() as FirestoreTable;
      
      // Check access permissions
      // For public access (when userId is empty/null), we still need to verify the table allows public access
      const userIdForAccess = userId || null;
      
      // Get user's email to pass to permission check
      const userEmail = auth.currentUser?.email || null;
      console.log(`[GetTable] Checking access for user ${userIdForAccess} with email ${userEmail}`);
      
      if (!await hasReadAccess(userIdForAccess, { ...data, id: tableId }, userEmail)) {
        throw new Error('Access denied: You do not have permission to view this table');
      }
      
      return {
        id: tableDoc.id,
        name: data.name,
        description: data.description || undefined,
        userId: data.userId,
        createdAt: data.createdAt.toDate().toISOString(),
        updatedAt: data.updatedAt.toDate().toISOString(),
        rows: data.rows,
        columns: data.columns,
        access: data.access,
        schedule: data.schedule
      };
    } catch (error) {
      console.error('Error getting table:', error);
      throw error;
    }
  },

  async createTable(userId: string, name: string, options?: { autoSetup?: boolean }): Promise<Table> {
    try {
      // Use the backend function to create the table
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      
      // Connect to emulator in development
      if (process.env.NODE_ENV === 'development') {
        const { connectFunctionsEmulator } = await import('firebase/functions');
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      
      const createTableFunction = httpsCallable(functions, 'create_table');
      
      const result = await createTableFunction({ 
        name, 
        autoSetup: options?.autoSetup ?? true // Default to true if not specified
      });
      const responseData = result.data as any;
      
      console.log(`[TableService] Created new table via backend - ID: ${responseData.id}`);

      // Return the table with the data from the backend
      return {
        id: responseData.id,
        name: responseData.name,
        userId: responseData.userId,
        createdAt: responseData.createdAt,
        updatedAt: responseData.updatedAt,
        access: responseData.access
      };
    } catch (error: any) {
      console.error('Error creating table:', error);
      
      // Handle specific error cases
      if (error.code === 'functions/unauthenticated') {
        throw new Error('You must be logged in to create a table');
      } else if (error.code === 'functions/invalid-argument') {
        throw new Error(error.message || 'Invalid table name');
      } else if (error.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to create tables');
      } else {
        throw new Error(error.message || 'Failed to create table');
      }
    }
  },

  async updateTableName(userId: string, tableId: string, newName: string): Promise<void> {
    try {
      if (!userId) throw new Error('User ID is required');
      if (!tableId) throw new Error('Table ID is required');
      if (!newName.trim()) throw new Error('Table name cannot be empty');
      
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        throw new Error(`Table with ID ${tableId} not found`);
      }
      
      const userEmail = auth.currentUser?.email || null;
      if (!await hasWriteAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Unauthorized: You do not have permission to modify this table');
      }

      await updateDoc(tableRef, {
        name: newName.trim(),
        updatedAt: serverTimestamp()
      });

      console.log(`[TableService] Updated table name - ID: ${tableId}, New name: ${newName}`);
    } catch (error) {
      console.error('Error updating table name:', error);
      throw error;
    }
  },

  async updateTableDescription(userId: string, tableId: string, newDescription: string): Promise<void> {
    try {
      if (!userId) throw new Error('User ID is required');
      if (!tableId) throw new Error('Table ID is required');
      
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        throw new Error(`Table with ID ${tableId} not found`);
      }
      
      const userEmail = auth.currentUser?.email || null;
      if (!await hasWriteAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Unauthorized: You do not have permission to modify this table');
      }

      await updateDoc(tableRef, {
        description: newDescription.trim() || null, // Store null for empty descriptions
        updatedAt: serverTimestamp()
      });

      console.log(`[TableService] Updated table description - ID: ${tableId}`);
    } catch (error) {
      console.error('Error updating table description:', error);
      throw error;
    }
  },

  async updateTableSchedule(userId: string, tableId: string, frequency: number): Promise<void> {
    try {
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        throw new Error('Table not found');
      }
      
      const userEmail = auth.currentUser?.email || null;
      if (!await hasOwnerAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Only table owners can configure automatic updates');
      }
      
      const tableData = tableDoc.data() as FirestoreTable;
      const updateData: any = {
        updatedAt: serverTimestamp()
      };
      
      if (frequency === 0) {
        // Disable scheduling by removing the schedule field
        updateData['schedule'] = null;
      } else {
        // Set or update the schedule
        const existingSchedule = tableData.schedule;
        updateData['schedule'] = {
          frequency: frequency,
          // Preserve existing lastUpdate if it exists, otherwise don't set it
          // The scheduled research function will handle tables without lastUpdate
          ...(existingSchedule?.lastUpdate ? { lastUpdate: existingSchedule.lastUpdate } : {})
        };
      }
      
      await updateDoc(tableRef, updateData);
    } catch (error) {
      console.error('Error updating table schedule:', error);
      throw error;
    }
  },

  async updateCell(userId: string, tableId: string, rowIndex: number, columnIndex: number, newValue: string, proof?: string, instructions?: string): Promise<Cell> {
    try {
      console.log(`[TableService] Updating cell - Row: ${rowIndex}, Column: ${columnIndex}, Value: ${newValue}`);
      
      // Validate inputs
      if (!userId) throw new Error('User ID is required');
      if (!tableId) throw new Error('Table ID is required');
      if (rowIndex < 0) throw new Error('Row index must be non-negative');
      if (columnIndex < 0) throw new Error('Column index must be non-negative');
      
      // First verify table ownership
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        throw new Error(`Table with ID ${tableId} not found`);
      }
      
      const userEmail = auth.currentUser?.email || null;
      if (!await hasWriteAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Unauthorized: You do not have permission to modify this table');
      }

      const cellsRef = collection(db, 'cells');
      const q = query(cellsRef, 
        where('tableId', '==', tableId),
        where('rowIndex', '==', rowIndex),
        where('columnIndex', '==', columnIndex)
      );
      
      const cellsSnapshot = await getDocs(q);
      const now = new Date().toISOString();

      // If the value is empty AND no instructions provided and the cell exists, delete it
      if (newValue === '' && (instructions === undefined || instructions.trim() === '') && !cellsSnapshot.empty) {
        const cellDocRef = cellsSnapshot.docs[0].ref;
        await deleteDoc(cellDocRef);
        console.log(`[TableService] Deleted cell with empty value - ID: ${cellDocRef.id}`);
        
        // Return the cell with empty value for UI update
        return {
          id: cellDocRef.id,
          value: '',
          proof: undefined,
          columnIndex,
          rowIndex,
          tableId,
          state: CellState.EMPTY,
          updatedAt: now
        };
      }
      
      // If the value is empty and no instructions provided and the cell doesn't exist, just return without creating
      if (newValue === '' && (instructions === undefined || instructions.trim() === '') && cellsSnapshot.empty) {
        console.log(`[TableService] Skipped creating cell with empty value`);
        return {
          id: 'temp-' + Date.now(), // Temporary ID for the UI
          value: '',
          proof: undefined,
          columnIndex,
          rowIndex,
          tableId,
          state: CellState.EMPTY,
          updatedAt: now
        };
      }

      // Prepare cell data with proper validation
      interface CellData {
        value: string;
        columnIndex: number;
        rowIndex: number;
        tableId: string;
        state: CellState;
        updatedAt: Timestamp;
        proof?: CellProof;
        instructions?: string | null;
      }

      const cellData: CellData = {
        value: newValue.trim(), // Trim whitespace
        columnIndex,
        rowIndex,
        tableId,
        state: CellState.HAS_VALUE,
        updatedAt: serverTimestamp() as Timestamp
      };

      // Only add proof if it exists
      if (proof) {
        cellData.proof = { url: proof, timestamp: new Date().toISOString() };
      }

      if (typeof instructions === 'string') {
        cellData.instructions = instructions.trim() || null;
      }

      let cellDocRef;
      try {
        if (!cellsSnapshot.empty) {
          // Update existing cell
          cellDocRef = cellsSnapshot.docs[0].ref;
          if (!proof && instructions === undefined) {
            // If no proof and no instructions, remove the field
            await updateDoc(cellDocRef, {
              ...cellData,
              proof: null, // Use null instead of undefined for Firestore
              instructions: null // Use null instead of undefined for Firestore
            } as Record<string, any>);
          } else {
            await updateDoc(cellDocRef, cellData as Record<string, any>);
          }
          console.log(`[TableService] Updated existing cell - ID: ${cellDocRef.id}`);
        } else {
          // Create new cell with auto-generated ID
          cellDocRef = await addDoc(cellsRef, {
            ...cellData,
            proof: proof ? cellData.proof : null, // Use null instead of undefined for new documents
            instructions: cellData.instructions || null // Use null instead of undefined for new documents
          } as Record<string, any>);
          console.log(`[TableService] Created new cell - ID: ${cellDocRef.id}`);
        }
      } catch (dbError: any) {
        console.error('Database operation failed:', dbError);
        throw new Error(`Failed to ${!cellsSnapshot.empty ? 'update' : 'create'} cell: ${dbError.message}`);
      }

      // Return the cell with ISO string date for immediate UI update
      return {
        id: cellDocRef.id,
        value: newValue,
        proof: cellData.proof,
        columnIndex,
        rowIndex,
        tableId,
        state: CellState.HAS_VALUE,
        instructions: cellData.instructions?.trim() || undefined,
        updatedAt: now
      };
    } catch (error: any) {
      console.error('Error updating cell:', error);
      // Rethrow with more context
      throw new Error(`Failed to update cell at row ${rowIndex}, column ${columnIndex}: ${error.message}`);
    }
  },

  async getUserTables(userId: string): Promise<Table[]> {
    try {
      const tablesRef = collection(db, 'tables');
      const q = query(
        tablesRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      // Get all tables first
      const tables = querySnapshot.docs.map(doc => {
        const data = doc.data() as FirestoreTable;
        return {
          id: doc.id,
          name: data.name,
          userId: data.userId,
          createdAt: data.createdAt.toDate().toISOString(),
          updatedAt: data.updatedAt.toDate().toISOString(),
          rows: 0,
          columns: 0,
          schedule: data.schedule
        };
      });

      // Get cell counts for each table
      const tablesWithCounts = await Promise.all(tables.map(async (table) => {
        const cellsRef = collection(db, 'cells');
        const cellsQuery = query(cellsRef, where('tableId', '==', table.id));
        const cellsSnapshot = await getDocs(cellsQuery);
        
        // Calculate max row and column indices
        let maxRow = -1;
        let maxCol = -1;
        
        cellsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          maxRow = Math.max(maxRow, data.rowIndex);
          maxCol = Math.max(maxCol, data.columnIndex);
        });

        return {
          ...table,
          rows: maxRow + 1,
          columns: maxCol + 1
        };
      }));

      return tablesWithCounts;
    } catch (error) {
      console.error('Error getting user tables:', error);
      throw error;
    }
  },

  async getCells(userId: string, tableId: string, startRow: number, startCol: number, rowCount: number, colCount: number): Promise<Cell[]> {
    try {
      // First verify table ownership
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      const userEmail = auth.currentUser?.email || null;
      if (!tableDoc.exists() || !await hasReadAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Table not found or unauthorized');
      }

      const cellsRef = collection(db, 'cells');
      const q = query(cellsRef, 
        where('tableId', '==', tableId),
        where('rowIndex', '>=', startRow),
        where('rowIndex', '<', startRow + rowCount),
        where('columnIndex', '>=', startCol),
        where('columnIndex', '<', startCol + colCount)
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data() as FirestoreCell;
        const proof = data.proof === null ? undefined : data.proof;
        return {
          id: doc.id,
          tableId: tableId,
          rowIndex: data.rowIndex,
          columnIndex: data.columnIndex,
          state: data.state || CellState.EMPTY,
          value: data.value,
          detailedValue: data.detailedValue,
          confidence: data.confidence,
          proof,
          error: data.error || undefined,
          updatedAt: data.updatedAt.toDate().toISOString(),
          updatedBy: data.updatedBy,
          instructions: data.instructions || undefined
        } as Cell;
      });
    } catch (error) {
      console.error('Error getting cells:', error);
      throw error;
    }
  },

  async getCellsBatch(userId: string, tableId: string, lastDocId: string | null): Promise<{ cells: Cell[]; lastDocId: string | null; hasMore: boolean }> {
    try {
      console.log(`[TableService] Fetching cells batch - TableID: ${tableId}, LastDocID: ${lastDocId || 'initial'}`);
      
      // First verify table ownership
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      const userEmail = auth.currentUser?.email || null;
      if (!tableDoc.exists() || !await hasReadAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Table not found or unauthorized');
      }

      const BATCH_SIZE = 200; // Increased batch size to ensure we can fill the initial viewport
      const cellsRef = collection(db, 'cells');
      let q;

      if (lastDocId) {
        const lastDocRef = doc(db, 'cells', lastDocId);
        const lastDocSnap = await getDoc(lastDocRef);
        
        if (!lastDocSnap.exists()) {
          console.warn(`[TableService] Last document with ID ${lastDocId} not found, starting from beginning`);
          q = query(
            cellsRef,
            where('tableId', '==', tableId),
            orderBy('updatedAt', 'desc'),
            limit(BATCH_SIZE)
          );
        } else {
          q = query(
            cellsRef,
            where('tableId', '==', tableId),
            orderBy('updatedAt', 'desc'),
            startAfter(lastDocSnap),
            limit(BATCH_SIZE)
          );
        }
      } else {
        q = query(
          cellsRef,
          where('tableId', '==', tableId),
          orderBy('updatedAt', 'desc'),
          limit(BATCH_SIZE)
        );
      }

      const cellsSnapshot = await getDocs(q);
      console.log(`[TableService] Fetched ${cellsSnapshot.size} cells in batch`);
      
      const cells = cellsSnapshot.docs.map(doc => {
        const data = doc.data() as FirestoreCell;
        const proof = data.proof === null ? undefined : data.proof;
        return {
          id: doc.id,
          tableId: tableId,
          rowIndex: data.rowIndex,
          columnIndex: data.columnIndex,
          state: data.state || CellState.EMPTY,
          value: data.value,
          detailedValue: data.detailedValue,
          confidence: data.confidence,
          proof,
          error: data.error || undefined,
          updatedAt: data.updatedAt.toDate().toISOString(),
          updatedBy: data.updatedBy,
          instructions: data.instructions || undefined
        } as Cell;
      });

      const lastVisible = cellsSnapshot.docs[cellsSnapshot.docs.length - 1];
      const hasMore = cellsSnapshot.docs.length === BATCH_SIZE;

      console.log(`[TableService] Processed ${cells.length} cells, hasMore: ${hasMore}`);
      if (cells.length > 0 && cells[0].value) {
        console.log(`[TableService] Sample cell - Row: ${cells[0].rowIndex}, Column: ${cells[0].columnIndex}, Value: ${cells[0].value.substring(0, 20)}`);
      }

      return {
        cells,
        lastDocId: lastVisible?.id || null,
        hasMore
      };
    } catch (error) {
      console.error('Error getting cells batch:', error);
      throw error;
    }
  },
  
  async getCellsByViewport(userId: string, tableId: string, startRow: number, endRow: number, startCol: number, endCol: number): Promise<Cell[]> {
    try {
      console.log(`[TableService] Fetching cells for viewport - Rows: ${startRow}-${endRow}, Columns: ${startCol}-${endCol}`);
      
      // First verify table access
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        throw new Error('Table not found');
      }
      
      const tableData = tableDoc.data() as FirestoreTable;
      // Handle public access by converting empty string to null
      const userIdForAccess = userId || null;
      const userEmail = auth.currentUser?.email || null;
      if (!await hasReadAccess(userIdForAccess, { ...tableData, id: tableId }, userEmail)) {
        throw new Error('Access denied: You do not have permission to view this table');
      }

      // We need to use multiple queries because Firestore doesn't support
      // compound queries with multiple range operators (>= and <=) on different fields
      const cellsRef = collection(db, 'cells');
      
      // Query for the viewport range
      const rowRangeQuery = query(
        cellsRef,
        where('tableId', '==', tableId),
        where('rowIndex', '>=', startRow),
        where('rowIndex', '<=', endRow)
      );
      
      const snapshot = await getDocs(rowRangeQuery);
      console.log(`[TableService] Found ${snapshot.size} cells in row range ${startRow}-${endRow}`);
      
      // Filter in memory for the column range
      const cells = snapshot.docs
        .map(doc => {
          const data = doc.data() as FirestoreCell;
          if (data.columnIndex >= startCol && data.columnIndex <= endCol) {
            const proof = data.proof === null ? undefined : data.proof;
            return {
              id: doc.id,
              tableId: tableId,
              rowIndex: data.rowIndex,
              columnIndex: data.columnIndex,
              state: data.state || CellState.EMPTY,
              value: data.value,
              detailedValue: data.detailedValue,
              confidence: data.confidence,
              proof,
              error: data.error || undefined,
              updatedAt: data.updatedAt.toDate().toISOString(),
              updatedBy: data.updatedBy,
              instructions: data.instructions || undefined
            } as Cell;
          }
          return null;
        })
        .filter((cell): cell is Cell => cell !== null);
      
      console.log(`[TableService] Returning ${cells.length} cells after column filtering ${startCol}-${endCol}`);
      return cells;
    } catch (error) {
      console.error('Error getting cells by viewport:', error);
      throw error;
    }
  },

  subscribeToCellUpdates(tableId: string, callback: (cells: Cell[], removedCellKeys: string[]) => void): () => void {
    console.log(`[TableService] Setting up real-time listener for table: ${tableId}`);
    
    if (!tableId) {
      console.error('[TableService] Cannot set up listener: tableId is empty');
      return () => {}; // Return a no-op unsubscribe function
    }
    
    const cellsRef = collection(db, 'cells');
    const q = query(cellsRef, where('tableId', '==', tableId));
    
    // Set up the listener
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updatedCells: Cell[] = [];
      const removedCellKeys: string[] = [];
      const cellLookup = new Map<string, { rowIndex: number; columnIndex: number }>();
      
      try {
        // First, build a lookup of all document IDs to their row/column indices
        snapshot.docs.forEach(doc => {
          const data = doc.data() as FirestoreCell;
          if (data && typeof data.rowIndex === 'number' && typeof data.columnIndex === 'number') {
            cellLookup.set(doc.id, { 
              rowIndex: data.rowIndex, 
              columnIndex: data.columnIndex 
            });
          }
        });
        
        // Process removed documents first
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') {
            const docId = change.doc.id;
            const cellData = change.doc.data() as FirestoreCell;
            
            // Create a key in the format "rowIndex_columnIndex"
            if (cellData && typeof cellData.rowIndex === 'number' && typeof cellData.columnIndex === 'number') {
              const cellKey = `${cellData.rowIndex}_${cellData.columnIndex}`;
              removedCellKeys.push(cellKey);
              console.log(`[TableService] Cell removed: ${docId} at position ${cellKey}`);
            } else {
              console.log(`[TableService] Cell removed: ${docId} but position data not available`);
            }
            return;
          }
        });

        // Then process added/modified documents
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') return; // Skip removed documents as they're already handled
          
          try {
            const data = change.doc.data() as FirestoreCell;
            
            // Skip if data is invalid
            if (!data || typeof data.rowIndex !== 'number' || typeof data.columnIndex !== 'number') {
              console.warn('[TableService] Skipping invalid cell data:', data);
              return;
            }
            
            // Convert Firestore timestamp to string
            const updatedAt = data.updatedAt instanceof Timestamp 
              ? data.updatedAt.toDate().toISOString() 
              : new Date().toISOString();
            
            // Create cell object
            const cell: Cell = {
              id: change.doc.id,
              tableId: tableId,
              rowIndex: data.rowIndex,
              columnIndex: data.columnIndex,
              state: data.state || CellState.EMPTY,
              value: typeof data.value === 'string' ? data.value : (data.value == null ? '' : String(data.value)),
              detailedValue: data.detailedValue,
              confidence: data.confidence,
              proof: data.proof || undefined,
              error: data.error || undefined,
              updatedAt,
              updatedBy: data.updatedBy,
              instructions: data.instructions || undefined
            };
            
            // For added or modified cells, add to updatedCells
            updatedCells.push(cell);
          } catch (err) {
            console.error('[TableService] Error processing document change:', err);
          }
        });
        
        if (updatedCells.length > 0 || removedCellKeys.length > 0) {
          console.log(`[TableService] Real-time update: ${updatedCells.length} cells changed, ${removedCellKeys.length} cells removed`);
          callback(updatedCells, removedCellKeys);
        }
      } catch (err) {
        console.error('[TableService] Error processing snapshot:', err);
        // Don't call callback with empty arrays on error, let the UI handle the error state
      }
    }, (error) => {
      console.error('[TableService] Error in real-time listener:', error);
      // Don't call callback with empty arrays on error, let the UI handle the error state
    });
    
    return unsubscribe;
  },

  subscribeToTableUpdates(tableId: string, callback: (table: Table) => void): () => void {
    console.log(`[TableService] Setting up real-time listener for table metadata: ${tableId}`);
    
    if (!tableId) {
      console.error('[TableService] Cannot set up table listener: tableId is empty');
      return () => {}; // Return a no-op unsubscribe function
    }
    
    const tableRef = doc(db, 'tables', tableId);
    
    // Set up the listener
    const unsubscribe = onSnapshot(tableRef, (docSnapshot) => {
      try {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as FirestoreTable;
          
          // Convert Firestore timestamps to ISO strings
          const table: Table = {
            id: docSnapshot.id,
            name: data.name,
            description: data.description || undefined,
            userId: data.userId,
            createdAt: data.createdAt.toDate().toISOString(),
            updatedAt: data.updatedAt.toDate().toISOString(),
            rows: data.rows,
            columns: data.columns,
            access: data.access,
            schedule: data.schedule
          };
          
          console.log(`[TableService] Table metadata updated: ${tableId}`);
          callback(table);
        } else {
          console.warn(`[TableService] Table document does not exist: ${tableId}`);
        }
      } catch (err) {
        console.error('[TableService] Error processing table snapshot:', err);
      }
    }, (error) => {
      console.error('[TableService] Error in table real-time listener:', error);
    });
    
    return unsubscribe;
  },

  async deleteColumn(userId: string, tableId: string, columnIndex: number): Promise<void> {
    try {
      // First verify table ownership
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      const userEmail = auth.currentUser?.email || null;
      if (!tableDoc.exists() || !await hasWriteAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Table not found or unauthorized');
      }

      // Get all cells in this column
      const cellsRef = collection(db, 'cells');
      const q = query(
        cellsRef,
        where('tableId', '==', tableId),
        where('columnIndex', '==', columnIndex)
      );
      
      const cellsSnapshot = await getDocs(q);
      
      // Delete cells in batches of 500 (Firestore batch limit)
      const batchSize = 500;
      const batches = Math.ceil(cellsSnapshot.size / batchSize);
      
      for (let i = 0; i < batches; i++) {
        const batch = writeBatch(db);
        const batchDocs = cellsSnapshot.docs.slice(i * batchSize, (i + 1) * batchSize);
        
        batchDocs.forEach(doc => {
          batch.delete(doc.ref);
        });
        
        // Add a delay between batches to allow the listener to process changes
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
   
        await batch.commit();
      }
    } catch (error) {
      console.error('Error deleting column:', error);
      throw error;
    }
  },

  async deleteRow(userId: string, tableId: string, rowIndex: number): Promise<void> {
    try {
      // First verify table ownership
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      const userEmail = auth.currentUser?.email || null;
      if (!tableDoc.exists() || !await hasWriteAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Table not found or unauthorized');
      }

      // Get all cells in this row
      const cellsRef = collection(db, 'cells');
      const q = query(
        cellsRef,
        where('tableId', '==', tableId),
        where('rowIndex', '==', rowIndex)
      );
      
      const cellsSnapshot = await getDocs(q);
      
      // Delete cells in batches of 500 (Firestore batch limit)
      const batchSize = 500;
      const batches = Math.ceil(cellsSnapshot.size / batchSize);
      
      for (let i = 0; i < batches; i++) {
        const batch = writeBatch(db);
        const batchDocs = cellsSnapshot.docs.slice(i * batchSize, (i + 1) * batchSize);
        
        batchDocs.forEach(doc => {
          batch.delete(doc.ref);
        });
        
        // Add a delay between batches to allow the listener to process changes
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
   
        await batch.commit();
      }
    } catch (error) {
      console.error('Error deleting row:', error);
      throw error;
    }
  },

  /**
   * Update table sharing permissions and access settings.
   * Caller must be an owner.
   */
  async updateSharing(userId: string, tableId: string, updates: Partial<{ permissions: TablePermission[]; access: TableAccessConfig }>): Promise<void> {
    try {
      if (!userId) throw new Error('User ID is required');
      
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        throw new Error('Table not found');
      }
      
      // Only owners can modify sharing permissions
      const userEmail = auth.currentUser?.email || null;
      if (!await hasOwnerAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail)) {
        throw new Error('Only table owners can modify sharing permissions');
      }

      // Update access settings if provided
      if (updates.access !== undefined) {
        const updateData: any = {
          access: updates.access,
          updatedAt: serverTimestamp()
        };
        await updateDoc(tableRef, updateData);
      }

      // Update permissions using deterministic IDs if provided
      if (updates.permissions !== undefined) {
        // Get existing permissions to know which ones to remove
        const existingPermissions = await getTableRoles(tableId);
        const existingEmails = new Set(existingPermissions.map(p => p.email));
        const newEmails = new Set(updates.permissions.map(p => p.email));
        
        // Remove permissions that are no longer in the new list
        const emailsToRemove = Array.from(existingEmails).filter(email => !newEmails.has(email));
        for (const email of emailsToRemove) {
          await removeUserTablePermission(tableId, email);
        }
        
        // Set/update permissions for all emails in the new list
        for (const permission of updates.permissions) {
          await setUserTablePermission(tableId, permission.email, permission.role, userId);
        }
      }
    } catch (error) {
      console.error('Error updating sharing:', error);
      throw error;
    }
  },

  /**
   * Get all tables a user has access to (owner, explicit permission, or public)
   */
     async getUserAccessibleTables(userId: string): Promise<Table[]> {
    try {
      const tables: Table[] = [];
      const userEmail = auth.currentUser?.email || null;

      // Get tables the user owns
      const ownedTablesRef = collection(db, 'tables');
      const ownedTablesQuery = query(ownedTablesRef, where('userId', '==', userId));
      const ownedSnapshot = await getDocs(ownedTablesQuery);
      
      const ownedTableIds = new Set<string>();
      
      ownedSnapshot.docs.forEach(doc => {
        const data = doc.data() as FirestoreTable;
        ownedTableIds.add(doc.id);
        tables.push({
          id: doc.id,
          name: data.name,
          description: data.description,
          userId: data.userId,
          createdAt: data.createdAt.toDate().toISOString(),
          updatedAt: data.updatedAt.toDate().toISOString(),
          rows: data.rows,
          columns: data.columns,
          access: data.access,
          schedule: data.schedule
        });
      });

      // Get tables the user has explicit permissions for (if they have an email)
      if (userEmail) {
        const permissionTableIds = await getUserPermissionTables(userEmail);
        
        for (const tableId of permissionTableIds) {
          if (!ownedTableIds.has(tableId)) {
            const tableRef = doc(db, 'tables', tableId);
            const tableDoc = await getDoc(tableRef);
            
            if (tableDoc.exists()) {
              const data = tableDoc.data() as FirestoreTable;
              tables.push({
                id: tableDoc.id,
                name: data.name,
                description: data.description,
                userId: data.userId,
                createdAt: data.createdAt.toDate().toISOString(),
                updatedAt: data.updatedAt.toDate().toISOString(),
                rows: data.rows,
                columns: data.columns,
                access: data.access,
                schedule: data.schedule
              });
            }
          }
        }
      }
      
      // Fallback: populate stats for tables missing row/column info (legacy docs)
      const tablesWithStats = await Promise.all(
        tables.map(async (tbl) => {
          if (tbl.rows && tbl.columns && tbl.rows > 0) {
            return tbl;
          }

          try {
            const cellsRef = collection(db, 'cells');
            const cellsQuery = query(cellsRef, where('tableId', '==', tbl.id));
            const snapshot = await getDocs(cellsQuery);

            let maxRow = -1;
            let maxCol = -1;

            snapshot.forEach((d) => {
              const data = d.data() as any;
              maxRow = Math.max(maxRow, data.rowIndex);
              maxCol = Math.max(maxCol, data.columnIndex);
            });

            return {
              ...tbl,
              rows: maxRow + 1,
              columns: maxCol + 1,
            } as Table;
          } catch (err) {
            console.warn('Failed to compute stats for table', tbl.id, err);
            return tbl;
          }
        })
      );

      return tablesWithStats;
    } catch (error) {
      console.error('Error getting user accessible tables:', error);
      throw error;
    }
  },

  /**
   * Check if user has owner access to a table
   */
  async hasOwnerAccess(userId: string, tableId: string): Promise<boolean> {
    try {
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        return false;
      }
      
      const userEmail = auth.currentUser?.email || null;
      return await hasOwnerAccess(userId, { ...tableDoc.data(), id: tableId }, userEmail);
    } catch (error) {
      console.error('Error checking owner access:', error);
      return false;
    }
  },

  /**
   * Get detailed table with permissions loaded
   */
  async getTableWithPermissions(userId: string, tableId: string): Promise<Table | null> {
    try {
      const table = await this.getTable(userId, tableId);
      if (!table) return null;

      const permissions: TablePermission[] = await getTableRoles(tableId);
      
      return {
        ...table,
        permissions
      };
    } catch (error: any) {
      console.error('Error getting table with permissions:', error);
      // Re-throw so callers can handle not-found vs permission-denied
      throw error;
    }
  },

  /**
   * Get the current user's permission level for a table
   */
  async getUserPermissionLevel(userId: string | null, tableId: string): Promise<'owner' | 'editor' | 'viewer' | null> {
    try {
      const tableRef = doc(db, 'tables', tableId);
      const tableDoc = await getDoc(tableRef);
      
      if (!tableDoc.exists()) {
        return null;
      }
      
      const userEmail = auth.currentUser?.email || null;
      return await getUserPermissionLevel(userId, { ...tableDoc.data(), id: tableId }, userEmail);
    } catch (error) {
      console.error('Error checking user permission level:', error);
      return null;
    }
  }
}; 